import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Send, ArrowLeft, Check, CheckCheck, Search, MessageSquare, ExternalLink, Package, MoreVertical, Trash2, Archive, ShieldOff, ShieldBan, ImagePlus, X, ArchiveRestore, Flag } from 'lucide-react';

export default function MessagesPage() {
  const { user, fetchUnreadCount } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, tc, lang } = useTranslation('messages');
  const locale = lang === 'nl' ? 'nl-NL' : 'fr-FR';
  const { theme } = useTheme();
  const selectedConvId = searchParams.get('conversation');

  const [conversations, setConversations] = useState([]);
  const [archivedConvs, setArchivedConvs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const contextMenuRef = useRef(null);

  const { sendMessage: wsSend, lastMessage } = useWebSocket();

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/conversations');
      setConversations(res.data);
    } catch (err) { console.error('Failed to load conversations:', err); } finally { setLoading(false); }
  }, []);

  const fetchArchived = useCallback(async () => {
    try {
      const res = await api.get('/conversations/archived');
      setArchivedConvs(res.data);
    } catch (err) { console.error('Failed to load archived conversations:', err); }
  }, []);

  useEffect(() => { fetchConversations(); fetchArchived(); }, [fetchConversations, fetchArchived]);

  useEffect(() => {
    if (!selectedConvId) return;
    api.get(`/conversations/${selectedConvId}/messages`)
      .then(r => {
        setMessages(r.data.messages || []);
        setTimeout(() => scrollToBottom(), 100);
        fetchUnreadCount();
      })
      .catch((err) => { console.error('Failed to load messages:', err); });
  }, [selectedConvId, fetchUnreadCount]);

  useEffect(() => {
    if (selectedConvId && inputRef.current) inputRef.current.focus();
  }, [selectedConvId]);

  useEffect(() => {
    if (!lastMessage) return;
    const data = lastMessage;
    if (data.type === 'new_message' || data.type === 'message_sent') {
      if (data.conversation_id === selectedConvId) {
        setMessages(prev => {
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        setTimeout(() => scrollToBottom(), 100);
      }
      fetchConversations();
    }
    if (data.type === 'user_online' || data.type === 'user_offline') {
      setConversations(prev => prev.map(c => {
        const otherId = c.buyer_id === user?.id ? c.seller_id : c.buyer_id;
        if (otherId === data.user_id) {
          return { ...c, other_user: { ...c.other_user, is_online: data.type === 'user_online' } };
        }
        return c;
      }));
    }
    if (data.type === 'messages_read' && data.conversation_id === selectedConvId) {
      setMessages(prev => prev.map(m =>
        m.sender_id === user?.id && !m.read ? { ...m, read: true } : m
      ));
    }
  }, [lastMessage, selectedConvId, user, fetchConversations]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) setContextMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConvId) return;
    const content = newMessage.trim();
    setNewMessage('');
    try {
      const res = await api.post(`/conversations/${selectedConvId}/messages`, { content });
      setMessages(prev => {
        if (prev.some(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setTimeout(() => scrollToBottom(), 100);
      fetchConversations();
    } catch {
      setNewMessage(content);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConvId) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = uploadRes.data.url || uploadRes.data.file_url;
      const res = await api.post(`/conversations/${selectedConvId}/messages`, { content: '', image: imageUrl });
      setMessages(prev => {
        if (prev.some(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setTimeout(() => scrollToBottom(), 100);
      fetchConversations();
    } catch {
      toast.error(t('sendError'));
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteConv = async (convId) => {
    try {
      await api.delete(`/conversations/${convId}`);
      toast.success(t('deleted'));
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (selectedConvId === convId) navigate('/messages');
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const handleArchiveConv = async (convId) => {
    try {
      await api.post(`/conversations/${convId}/archive`);
      toast.success(t('archived'));
      fetchConversations();
      fetchArchived();
      if (selectedConvId === convId) navigate('/messages');
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const handleUnarchiveConv = async (convId) => {
    try {
      await api.post(`/conversations/${convId}/unarchive`);
      toast.success(t('unarchived'));
      fetchConversations();
      fetchArchived();
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const handleBlockUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/block`);
      toast.success(t('blocked'));
      fetchConversations();
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const handleUnblockUser = async (userId) => {
    try {
      await api.post(`/users/${userId}/unblock`);
      toast.success(t('unblocked'));
      fetchConversations();
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const handleReportConv = async (convId) => {
    const reason = window.prompt(t('reportReason'));
    if (!reason?.trim()) return;
    try {
      await api.post(`/conversations/${convId}/report`, { reason: reason.trim() });
      toast.success(t('reported'));
    } catch { toast.error(tc('error')); }
    setContextMenu(null);
  };

  const selectedConv = [...conversations, ...archivedConvs].find(c => c.id === selectedConvId);
  const getName = (conv) => {
    if (conv.other_user) return `${conv.other_user.first_name || ''} ${conv.other_user.last_name || ''}`.trim() || '?';
    return '?';
  };
  const getInitials = (conv) => {
    if (!conv.other_user) return '?';
    const f = conv.other_user.first_name?.[0] || '';
    const l = conv.other_user.last_name?.[0] || '';
    return (f + l).toUpperCase() || '?';
  };
  const isOnline = (conv) => conv.other_user?.is_online || false;
  const getListingTitle = (conv) => conv.listing_info?.title || '';
  const getListingId = (conv) => conv.listing_id || '';
  const getOtherId = (conv) => conv.buyer_id === user?.id ? conv.seller_id : conv.buyer_id;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return t('today');
    if (d.toDateString() === yesterday.toDateString()) return t('yesterday');
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const formatConvDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return formatTime(dateStr);
    if (d.toDateString() === yesterday.toDateString()) return t('yesterday');
    return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const dateKey = new Date(msg.created_at).toDateString();
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(msg);
    return groups;
  }, {});

  const displayedConvs = showArchived ? archivedConvs : conversations;
  const filteredConversations = displayedConvs.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return getName(c).toLowerCase().includes(q) || getListingTitle(c).toLowerCase().includes(q);
  });

  if (!user) { navigate('/connexion'); return null; }

  const chatBg = theme === 'dark'
    ? { backgroundColor: '#0f172a' }
    : { backgroundColor: '#f0f4f8', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'60\' height=\'60\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'0.8\' fill=\'%231F3F72\' opacity=\'0.06\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'60\' height=\'60\' fill=\'url(%23p)\'/%3E%3C/svg%3E")' };

  const isBlocked = selectedConv?.is_blocked;

  const renderConvItem = (conv, isArchived = false) => {
    const name = getName(conv);
    const initials = getInitials(conv);
    const online = isOnline(conv);
    const listing = getListingTitle(conv);
    const isActive = conv.id === selectedConvId;
    const hasUnread = conv.unread_count > 0;
    const otherId = getOtherId(conv);
    return (
      <div key={conv.id} className="relative group">
        <button
          onClick={() => navigate(`/messages?conversation=${conv.id}`)}
          className={`w-full flex items-center gap-4 px-5 py-5 text-left transition-all duration-150 ${
            isActive
              ? 'bg-[#1F3F72]/[0.07] dark:bg-[#1F3F72]/20 border-l-[3px] border-l-[#1F3F72]'
              : 'hover:bg-neutral-50 dark:hover:bg-white/[0.03] border-l-[3px] border-l-transparent'
          }`}
          data-testid={`conversation-${conv.id}`}
        >
          <div className="relative shrink-0">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-[15px] font-bold tracking-wide transition-colors ${
              isActive
                ? 'bg-[#1F3F72] text-white shadow-md shadow-[#1F3F72]/20'
                : 'bg-[#1F3F72]/10 dark:bg-[#1F3F72]/25 text-[#1F3F72] dark:text-[#93b4e0]'
            }`}>
              {initials}
            </div>
            {online && !isArchived && (
              <span className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-[2.5px] border-white dark:border-[#111827] shadow-sm" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className={`text-[17px] truncate ${hasUnread ? 'font-bold text-neutral-900 dark:text-white' : 'font-semibold text-neutral-700 dark:text-neutral-300'}`}>
                {name}
                {conv.is_blocked && <ShieldBan className="inline w-4 h-4 ml-1.5 text-red-400" />}
              </p>
              <span className={`text-[13px] shrink-0 tabular-nums ${hasUnread ? 'text-[#F25C05] font-bold' : 'text-neutral-400 dark:text-neutral-500'}`}>{formatConvDate(conv.last_message_at)}</span>
            </div>
            {listing && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <Package className="w-4 h-4 text-[#F25C05] shrink-0" />
                <p className="text-[15px] text-[#F25C05] font-semibold truncate">{listing}</p>
              </div>
            )}
            <p className={`text-[15px] truncate leading-snug ${hasUnread ? 'text-neutral-600 dark:text-neutral-300 font-medium' : 'text-neutral-400 dark:text-neutral-500'}`}>{conv.last_message}</p>
          </div>
          {hasUnread && (
            <span className="bg-[#F25C05] text-white text-[11px] font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-2 shrink-0 shadow-sm shadow-[#F25C05]/30" data-testid={`unread-${conv.id}`}>
              {conv.unread_count}
            </span>
          )}
        </button>
        {/* Context menu button */}
        <button
          onClick={(e) => { e.stopPropagation(); setContextMenu(contextMenu === conv.id ? null : conv.id); }}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 opacity-0 group-hover:opacity-100 transition-all"
          data-testid={`conv-menu-${conv.id}`}
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {/* Context menu dropdown */}
        {contextMenu === conv.id && (
          <div ref={contextMenuRef} className="absolute top-10 right-3 z-50 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-lg py-1 w-48" data-testid={`conv-context-menu-${conv.id}`}>
            {isArchived ? (
              <button onClick={() => handleUnarchiveConv(conv.id)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors" data-testid="conv-unarchive-btn">
                <ArchiveRestore className="w-4 h-4" /> {t('unarchive')}
              </button>
            ) : (
              <button onClick={() => handleArchiveConv(conv.id)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors" data-testid="conv-archive-btn">
                <Archive className="w-4 h-4" /> {t('archive')}
              </button>
            )}
            {conv.is_blocked ? (
              <button onClick={() => handleUnblockUser(otherId)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-emerald-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors" data-testid="conv-unblock-btn">
                <ShieldOff className="w-4 h-4" /> {t('unblock')}
              </button>
            ) : (
              <button onClick={() => handleBlockUser(otherId)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors" data-testid="conv-block-btn">
                <ShieldBan className="w-4 h-4" /> {t('block')}
              </button>
            )}
            <button onClick={() => handleReportConv(conv.id)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors" data-testid="conv-report-btn">
              <Flag className="w-4 h-4" /> {t('report')}
            </button>
            <button onClick={() => handleDeleteConv(conv.id)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-testid="conv-delete-btn">
              <Trash2 className="w-4 h-4" /> {t('delete')}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-64px)] flex overflow-hidden bg-neutral-100 dark:bg-[#0c1222]" data-testid="messages-page">

      {/* Sidebar */}
      <div className={`w-full md:w-[420px] lg:w-[480px] flex flex-col border-r border-neutral-200 dark:border-neutral-700/60 bg-white dark:bg-[#111827] ${selectedConvId ? 'hidden md:flex' : 'flex'}`} data-testid="conversations-list">

        {/* Sidebar header */}
        <div className="px-5 pt-5 pb-4 border-b border-neutral-100 dark:border-neutral-700/40">
          <h1 className="text-xl font-bold text-[#1F3F72] dark:text-white tracking-tight mb-3">{t('title')}</h1>
          <div className="relative mb-3">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-neutral-400 dark:text-neutral-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full pl-11 pr-4 py-3 text-[15px] rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-[#1e293b] outline-none focus:border-[#1F3F72] focus:ring-2 focus:ring-[#1F3F72]/10 dark:focus:ring-[#1F3F72]/20 text-neutral-800 dark:text-neutral-200 placeholder-neutral-400 dark:placeholder-neutral-500 transition-colors"
              data-testid="conversation-search"
            />
          </div>
          {/* Tabs */}
          <div className="flex gap-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-1">
            <button
              onClick={() => setShowArchived(false)}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${!showArchived ? 'bg-white dark:bg-neutral-700 text-[#1F3F72] dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400'}`}
              data-testid="tab-conversations"
            >
              {t('conversations')} {conversations.length > 0 && <span className="ml-1 text-xs opacity-60">({conversations.length})</span>}
            </button>
            <button
              onClick={() => { setShowArchived(true); fetchArchived(); }}
              className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${showArchived ? 'bg-white dark:bg-neutral-700 text-[#1F3F72] dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400'}`}
              data-testid="tab-archived"
            >
              {t('archivedTab')} {archivedConvs.length > 0 && <span className="ml-1 text-xs opacity-60">({archivedConvs.length})</span>}
            </button>
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-neutral-200 dark:border-neutral-600 border-t-[#1F3F72]" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-20 px-6" data-testid="no-conversations">
              <MessageSquare className="w-12 h-12 text-neutral-200 dark:text-neutral-600 mx-auto mb-3" />
              <p className="text-sm text-neutral-400 dark:text-neutral-500">{searchQuery ? t('noResults') : t('noConversations')}</p>
            </div>
          ) : (
            filteredConversations.map((conv) => renderConvItem(conv, showArchived))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className={`flex-1 flex flex-col min-w-0 ${selectedConvId ? 'flex' : 'hidden md:flex'}`} data-testid="chat-area">
        {selectedConvId && selectedConv ? (
          <>
            {/* Chat header */}
            <div className="px-5 py-3 flex items-center gap-4 bg-white dark:bg-[#111827] border-b border-neutral-200 dark:border-neutral-700/60" data-testid="chat-header">
              <button onClick={() => navigate('/messages')} className="md:hidden p-1.5 -ml-2 rounded-lg text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-700 transition-colors" data-testid="chat-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <div className="relative shrink-0">
                    <div className="w-10 h-10 rounded-full bg-[#1F3F72] text-white flex items-center justify-center text-[13px] font-bold shadow-sm shadow-[#1F3F72]/20">
                      {getInitials(selectedConv)}
                    </div>
                    {isOnline(selectedConv) && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-[#111827]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[16px] font-bold text-neutral-900 dark:text-white leading-tight">{getName(selectedConv)}</p>
                    {isOnline(selectedConv) && (
                      <p className="text-[12px] text-emerald-600 dark:text-emerald-400 font-medium">{t('online')}</p>
                    )}
                  </div>
                </div>
                {getListingTitle(selectedConv) && getListingId(selectedConv) && (
                  <Link
                    to={`/annonces/${getListingId(selectedConv)}`}
                    className="flex items-center gap-2 bg-[#F25C05]/[0.06] dark:bg-[#F25C05]/10 border border-[#F25C05]/20 dark:border-[#F25C05]/25 rounded-lg px-3 py-1.5 hover:bg-[#F25C05]/[0.12] dark:hover:bg-[#F25C05]/20 transition-all group max-w-full"
                    data-testid="chat-listing-link"
                  >
                    <Package className="w-3.5 h-3.5 text-[#F25C05] shrink-0" />
                    <p className="text-[13px] font-bold text-[#F25C05] truncate group-hover:underline transition-colors">
                      {getListingTitle(selectedConv)}
                    </p>
                    <ExternalLink className="w-3 h-3 text-[#F25C05]/40 shrink-0 group-hover:text-[#F25C05] transition-all" />
                  </Link>
                )}
              </div>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto" style={chatBg} data-testid="messages-container">
              <div className="max-w-3xl mx-auto px-5 py-4">
                {Object.entries(groupedMessages).map(([dateKey, msgs]) => (
                  <div key={dateKey}>
                    <div className="flex items-center justify-center my-6">
                      <span className="text-[12px] text-neutral-500 dark:text-neutral-400 bg-white dark:bg-[#1e293b] px-5 py-1.5 rounded-full shadow-sm border border-neutral-200/60 dark:border-neutral-700/40 font-semibold">
                        {formatDate(msgs[0]?.created_at)}
                      </span>
                    </div>
                    {msgs.map((msg) => {
                      const isMine = msg.sender_id === user.id;
                      return (
                        <div key={msg.id} className={`flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                          <div className={`relative max-w-[75%] px-4 py-2.5 shadow-sm ${
                            isMine
                              ? 'bg-[#1F3F72] text-white rounded-2xl rounded-br-md'
                              : 'bg-white dark:bg-[#1e293b] text-neutral-800 dark:text-neutral-100 rounded-2xl rounded-bl-md border border-neutral-200/60 dark:border-neutral-700/40'
                          }`}>
                            {msg.image && (
                              <a href={msg.image} target="_blank" rel="noopener noreferrer" className="block mb-2">
                                <img src={msg.image} alt="" className="rounded-lg max-w-full max-h-64 object-cover" />
                              </a>
                            )}
                            {msg.content && <p className="text-[15px] leading-relaxed whitespace-pre-line pr-14">{msg.content}</p>}
                            <div className={`absolute bottom-2 right-3 flex items-center gap-1 ${
                              isMine ? 'text-white/50' : 'text-neutral-400 dark:text-neutral-500'
                            }`}>
                              <span className="text-[11px] tabular-nums">{formatTime(msg.created_at)}</span>
                              {isMine && (
                                msg.read
                                  ? <CheckCheck className="w-3.5 h-3.5 text-[#F25C05]" />
                                  : <Check className="w-3.5 h-3.5 text-white/40" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input bar */}
            <div className="bg-white dark:bg-[#111827] border-t border-neutral-200 dark:border-neutral-700/60 px-4 py-3">
              {isBlocked ? (
                <p className="text-center text-sm text-red-400 font-medium py-2" data-testid="blocked-message">{t('blockedMessage')}</p>
              ) : (
                <form onSubmit={handleSend} className="max-w-3xl mx-auto flex items-center gap-3" data-testid="message-form">
                  <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageUploading}
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-neutral-400 hover:text-[#1F3F72] hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all shrink-0"
                    data-testid="send-image-btn"
                    title={t('sendImage')}
                  >
                    {imageUploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-neutral-300 border-t-[#1F3F72]" />
                    ) : (
                      <ImagePlus className="w-5 h-5" />
                    )}
                  </button>
                  <input
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('messagePlaceholder')}
                    className="flex-1 bg-neutral-50 dark:bg-[#1e293b] rounded-xl px-5 py-3 text-[15px] border border-neutral-200 dark:border-neutral-600/50 outline-none focus:border-[#1F3F72] focus:ring-2 focus:ring-[#1F3F72]/10 dark:focus:ring-[#1F3F72]/20 text-neutral-800 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 transition-all"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 shrink-0 ${
                      newMessage.trim()
                        ? 'bg-[#F25C05] text-white shadow-md shadow-[#F25C05]/25 hover:bg-[#d94f04] hover:shadow-lg hover:shadow-[#F25C05]/30 active:scale-95'
                        : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                    }`}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-[18px] h-[18px]" />
                  </button>
                </form>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-neutral-50 dark:bg-[#0c1222]" data-testid="no-conversation-selected">
            <div className="w-24 h-24 rounded-2xl bg-[#1F3F72]/[0.08] dark:bg-[#1F3F72]/20 flex items-center justify-center mb-5">
              <MessageSquare className="w-10 h-10 text-[#1F3F72]/40 dark:text-[#93b4e0]/50" />
            </div>
            <p className="text-neutral-600 dark:text-neutral-300 text-base font-semibold">{t('selectConversation')}</p>
            <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-1.5">{t('selectConversationSub')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
